<template>
  <div class="flex flex-col gap-6">
    <div v-if="!isValidTaskTemplateId" class="text-sm text-destructive">
      Invalid task template id.
    </div>
    <div v-else-if="taskTemplateStore.isLoading && !taskTemplate" class="text-sm text-muted-foreground">
      Loading task template...
    </div>
    <div v-else-if="errorMessage" class="text-sm text-destructive">
      {{ errorMessage }}
    </div>
    <div v-else-if="taskTemplate" class="grid grid-cols-1 gap-4 xl:grid-cols-3">
      <TaskTemplateActionBar
        :task-template="taskTemplate"
        context="task-template-show"
        class="xl:col-span-3"
        @launched="onTaskTemplateLaunched"
      />

      <section class="rounded-lg border bg-card p-6 xl:col-span-2">
        <h1 class="text-xl font-semibold">
          {{ taskTemplate.title || 'Untitled task template' }}
        </h1>
        <p class="mt-2 text-sm text-muted-foreground">
          <template v-for="(segment, index) in templateDescriptionSegments" :key="`${segment.type}-${index}`">
            <a
              v-if="segment.type === 'link'"
              :href="segment.href"
              target="_blank"
              rel="noopener noreferrer"
              class="text-primary underline-offset-4 hover:underline"
            >{{ segment.text }}</a>
            <span v-else>{{ segment.text }}</span>
          </template>
        </p>

        <div class="mt-4 grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
          <p><span class="font-medium">ID:</span> {{ taskTemplate.id }}</p>
          <p>
            <span class="font-medium">Priority:</span>
            {{ taskTemplate.priority || '-' }}
          </p>
          <p>
            <span class="font-medium">Created By:</span>
            {{ taskTemplate.created_by_user?.name || taskTemplate.created_by_user_id || '-' }}
          </p>
          <p>
            <span class="font-medium">Assigned To:</span>
            {{ taskTemplate.assigned_to_user?.name || taskTemplate.assigned_to_user_id || '-' }}
          </p>
          <p>
            <span class="font-medium">Default Deadline:</span>
            {{ formatDefaultDeadline(taskTemplate.default_deadline_after_minutes) }}
          </p>
          <p>
            <span class="font-medium">Start At:</span>
            {{ formatDateTime(taskTemplate.start_at) }}
          </p>
          <p>
            <span class="font-medium">Private:</span>
            {{ taskTemplate.private ? 'Yes' : 'No' }}
          </p>
          <p>
            <span class="font-medium">Generated Tasks:</span>
            {{ taskTemplate.tasks?.length ?? 0 }}
          </p>
        </div>
      </section>

      <div class="flex flex-col gap-4">
        <ScheduleCard
          :schedule="primarySchedule"
          :is-saving="isUpdatingSchedule"
          :is-toggling-active="isUpdatingScheduleStatus"
          :save-schedule="saveTaskTemplateSchedule"
          :clear-schedule-handler="clearTaskTemplateSchedule"
          :toggle-active-handler="toggleTaskTemplateScheduleActive"
        />

        <TaskTemplateVariablesPanel :task-template="taskTemplate" />
      </div>

      <ChecklistTemplatesCard
        class="xl:col-span-3"
        :redirect-to-edit-page-after-create="false"
        :checklist-templateable-id="taskTemplate.id"
        :checklist-templateable-type="taskTemplateChecklistableType"
      />
    </div>
    <p v-else class="text-sm text-muted-foreground">
      Task template not found.
    </p>
  </div>
</template>

<script setup>
import { computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'

import TaskTemplateActionBar from '../components/task-templates/TaskTemplateActionBar.vue'
import TaskTemplateVariablesPanel from '../components/task-templates/TaskTemplateVariablesPanel.vue'
import { useTaskTemplateStore } from '../stores/taskTemplateStore.js'
import { formatTaskTemplateDeadlineLabel } from '../utils/formatTaskTemplateDeadline.js'
import { buildLinkifiedTextSegments } from '../utils/linkifyText.js'
import ChecklistTemplatesCard from '../../../../checklists/resources/js/components/checklist-templates/ChecklistTemplatesCard.vue'
import ScheduleCard from '../../../../utils/resources/js/components/schedules/ScheduleCard.vue'

const route = useRoute()
const taskTemplateStore = useTaskTemplateStore('task-template-show')
const taskTemplateChecklistableType = 'Umisis\\Tasks\\Models\\TaskTemplate'

const taskTemplateId = computed(() => Number(route.params.taskTemplate ?? 0))
const isValidTaskTemplateId = computed(
  () => Number.isInteger(taskTemplateId.value) && taskTemplateId.value > 0
)

const taskTemplate = computed(() => {
  if (!isValidTaskTemplateId.value) {
    return null
  }

  return taskTemplateStore.getItemById(taskTemplateId.value) ?? null
})

const primarySchedule = computed(() => {
  const schedules = taskTemplate.value?.schedules

  if (!Array.isArray(schedules) || schedules.length === 0) {
    return null
  }

  return schedules[0]
})
const templateDescriptionText = computed(() => {
  const description = typeof taskTemplate.value?.description === 'string'
    ? taskTemplate.value.description.trim()
    : ''

  return description !== '' ? description : 'No description provided.'
})
const templateDescriptionSegments = computed(() => buildLinkifiedTextSegments(templateDescriptionText.value))
const isUpdatingSchedule = computed(() => taskTemplateStore.isUpdatingSchedule(taskTemplateId.value))
const isUpdatingScheduleStatus = computed(() => taskTemplateStore.isUpdatingScheduleStatus(taskTemplateId.value))

const errorMessage = computed(() => {
  const message = taskTemplateStore.error?.response?.data?.message

  if (typeof message === 'string' && message.trim() !== '') {
    return message
  }

  if (taskTemplateStore.error && !taskTemplateStore.isLoading) {
    return 'Unable to load task template.'
  }

  return ''
})

const formatDateTime = (value) => {
  if (typeof value !== 'string' || value.trim() === '') {
    return '-'
  }

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return '-'
  }

  return date.toLocaleString()
}

const formatDefaultDeadline = (value) => {
  return formatTaskTemplateDeadlineLabel(value) ?? '-'
}

const fetchTaskTemplate = async () => {
  if (!isValidTaskTemplateId.value) {
    return
  }

  try {
    await taskTemplateStore.fetchItemById(taskTemplateId.value)
  } catch {
    // Generic store handles error state.
  }
}

const saveTaskTemplateSchedule = async (schedulePayload) => {
  if (!isValidTaskTemplateId.value) {
    return
  }

  const existingContext = primarySchedule.value?.pivot?.context
  const normalizedContext = typeof existingContext === 'string' && existingContext.trim() !== ''
    ? existingContext.trim()
    : 'task-template-launch'

  await taskTemplateStore.updateSchedule(taskTemplateId.value, {
    ...schedulePayload,
    context: normalizedContext,
    is_active: primarySchedule.value?.pivot?.is_active ?? true,
  })
}

const clearTaskTemplateSchedule = async () => {
  if (!isValidTaskTemplateId.value) {
    return
  }

  await taskTemplateStore.clearSchedule(taskTemplateId.value)
}

const toggleTaskTemplateScheduleActive = async (isActive) => {
  if (!isValidTaskTemplateId.value) {
    return
  }

  await taskTemplateStore.updateScheduleStatus(taskTemplateId.value, isActive)
}

onMounted(() => {
  fetchTaskTemplate()
})

watch(taskTemplateId, () => {
  fetchTaskTemplate()
})

const onTaskTemplateLaunched = async () => {
  await fetchTaskTemplate()
}
</script>
